import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import './SafetyCoefficients.scss';
import { ISafetyCoefficientsProps } from './ISafetyCoefficients';
import Grid from '@mui/material/Grid';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import Select, { SelectProps } from '@mui/material/Select';
import { setSafetyCoefficients } from '../../../store/project/projectslice';
import { assign, debounce } from 'lodash';
import { RootState } from '../../../../../reduxService';
import { updateValidation } from '../../../../Macro1/store/project/resultsSlice';
import { selectProjectInfo, selectMacro1 } from '../../../../store/projectInfo/projectInfoSlice';
import imgExternalLoads from '../../../../images/macro1/externalLoads.png';
import imgSlopeSurfaceMorphology from '../../../../images/macro1/slopeSurfaceMorphology.png';
import imgUncertaintyRockBehaviour from '../../../../images/macro1/uncertaintyRockBehaviour.png';
import imgUncertaintyRockMassUnit from '../../../../images/macro1/uncertaintyRockMassUnit.png';
import imgUncertaintyThicknessSurficialStability from '../../../../images/macro1/uncertaintyThicknessSurficialStability.png';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';

import Dialog from '../../../../../components/Dialog/Dialog';

export interface ISafetyCoefficientsForm {
  uncertaintyThicknessSurficialInstability: number,
  uncertaintyRockMassUnitWeight: number,
  uncertaintyRockBehaviorAndWeathering: number,
  safetyCoefficientReduceStabilizingForceCustom: boolean,
  gR: number,
  slopeSurfaceMorphology: number,
  externalLoads: number,
  safetyCoefficientIncreaseDrivingForceCustom: boolean,
  yD: number,
  yDF: number,
  coefficientPunchResistance: number,
  coefficientYieldStressBar: number,
  coefficientBondStress: number,
}

export const SafetyCoefficientsDefault: ISafetyCoefficientsForm = {
  uncertaintyThicknessSurficialInstability: 1.25,
  uncertaintyRockMassUnitWeight: 1,
  uncertaintyRockBehaviorAndWeathering: 1,
  safetyCoefficientReduceStabilizingForceCustom: false,
  gR: 1.25,
  slopeSurfaceMorphology: 1.2,
  externalLoads: 1,
  safetyCoefficientIncreaseDrivingForceCustom: false,
  yD: 1.2,
  yDF: 1.5,
  coefficientPunchResistance: 1.5,
  coefficientYieldStressBar: 1.15,
  coefficientBondStress: 1.8,
};

const MuiSelect = (props: SelectProps) => (
  <Select {...props} />
);


const SafetyCoefficients = (props: ISafetyCoefficientsProps) => {
  /*     const project = useAppSelector(selectProject); */
  const projectInfo = useAppSelector(selectProjectInfo);
  /*     const result = useAppSelector(selectResults); */
  const { t } = useTranslation();
  let timer = React.useRef<NodeJS.Timeout | null>(null);
  const macro1 = useAppSelector(selectMacro1);

  const [SafetyCoefficientsPayload, setSafetyCoefficientsPayload] = React.useState<any>(SafetyCoefficientsDefault);
  let _SafetyCoefficientsPayload = SafetyCoefficientsPayload;

  const [CurrentDialog, setCurrentDialog] = React.useState<string>("");
  const openDialog = (dialogCode: string) => {
    setCurrentDialog(dialogCode);
  }
  const closeDialog = () => {
    setCurrentDialog('');
  }

  const handleValueChanged = (valueKey: string, value: any, noUpdate: Boolean = false) => {
    assign(_SafetyCoefficientsPayload, { [valueKey]: value });
    if (!noUpdate) {
      setSafetyCoefficientsPayload({ ..._SafetyCoefficientsPayload });
    }
  }

  const SafetyCoefficientsSchema = Yup.object().shape({
    uncertaintyThicknessSurficialInstability: Yup.number().required().min(0.01).label(t('safety_coefficient_uncertainty_thickness_surficial_instability', { unit_measure: '' })),
    uncertaintyRockMassUnitWeight: Yup.number().required().min(0.01).label(t('safety_coefficient_uncertainty_rock_mass_unit_weight', { unit_measure: '' })),
    uncertaintyRockBehaviorAndWeathering: Yup.number().required().min(0.01).label(t('safety_coefficient_uncertainty_rock_behavior_and_weathering', { unit_measure: '' })),
    safetyCoefficientReduceStabilizingForceCustom: Yup.boolean().label(t('safety_coefficient_reduce_stabilizing_force_custom', { unit_measure: '' })),
    gR: Yup.number().min(1).required().label(t('safety_coefficient_gR', { unit_measure: '' })),
    slopeSurfaceMorphology: Yup.number().required().min(0.01).label(t('safety_coefficient_slope_surface_morphology', { unit_measure: '' })),
    externalLoads: Yup.number().required().min(0.01).label(t('safety_coefficient_external_loads', { unit_measure: '' })),
    safetyCoefficientIncreaseDrivingForceCustom: Yup.boolean().label(t('safety_coefficient_increase_driving_force_custom', { unit_measure: '' })),
    yD: Yup.number().min(1).required().label(t('safety_coefficient_yD', { unit_measure: '' })),
    yDF: Yup.number().required().label(t('safety_coefficient_yDF', { unit_measure: '' })),
    coefficientPunchResistance: Yup.number().required().min(0.01).label(t('safety_coefficient_coefficient_punch_resistance', { unit_measure: '' })),
    coefficientYieldStressBar: Yup.number().required().min(1.15).label(t('safety_coefficient_coefficient_yield_stress_bar', { unit_measure: '' })),
    coefficientBondStress: Yup.number().required().min(1.8).label(t('safety_coefficient_coefficient_bond_stress', { unit_measure: '' })),
  });

  const dispatch = useAppDispatch();
  const dispatchSafetyCoefficients = (values: ISafetyCoefficientsForm) => {
    dispatch(setSafetyCoefficients(values));
  }

  const formikProps = useFormik({
    initialValues: macro1.project?.data?.safetyCoefficients ?? SafetyCoefficientsDefault,
    validationSchema: SafetyCoefficientsSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (
      !formikProps.values.safetyCoefficientReduceStabilizingForceCustom
      && !Number.isNaN(formikProps.values.uncertaintyThicknessSurficialInstability)
      && !Number.isNaN(formikProps.values.uncertaintyRockMassUnitWeight)
      && !Number.isNaN(formikProps.values.uncertaintyRockBehaviorAndWeathering)) {
      calculateGR();
    }
    if (!formikProps.values.safetyCoefficientIncreaseDrivingForceCustom
      && !Number.isNaN(formikProps.values.slopeSurfaceMorphology)
      && !Number.isNaN(formikProps.values.externalLoads)) {
      calculateYD();
    }
  }, []);

  useEffect(() => {
    dispatchSafetyCoefficients(formikProps.values);
  }, [formikProps.values]);

  const calculateGR = () => {
    if (
      !formikProps.values.safetyCoefficientReduceStabilizingForceCustom
      && !Number.isNaN(formikProps.values.uncertaintyThicknessSurficialInstability)
      && !Number.isNaN(formikProps.values.uncertaintyRockMassUnitWeight)
      && !Number.isNaN(formikProps.values.uncertaintyRockBehaviorAndWeathering)) {
      formikProps.setFieldValue('gR',
        Number((Number(formikProps.values.uncertaintyThicknessSurficialInstability)
          * Number(formikProps.values.uncertaintyRockMassUnitWeight)
          * Number(formikProps.values.uncertaintyRockBehaviorAndWeathering)).toFixed(2))
      );
    }
  }

  useEffect(debounce(() => {
    calculateGR();
  }, 200), [formikProps.values.safetyCoefficientReduceStabilizingForceCustom
    , formikProps.values.uncertaintyThicknessSurficialInstability
    , formikProps.values.uncertaintyRockMassUnitWeight
    , formikProps.values.uncertaintyRockBehaviorAndWeathering]);

const calculateYD = () => {
  formikProps.setFieldValue('yD',
    Number((Number(formikProps.values.slopeSurfaceMorphology)
      * Number(formikProps.values.externalLoads)).toFixed(2))
  );
}

useEffect(debounce(() => {
  if (!formikProps.values.safetyCoefficientIncreaseDrivingForceCustom
    && !Number.isNaN(formikProps.values.slopeSurfaceMorphology)
    && !Number.isNaN(formikProps.values.externalLoads)) {
    calculateYD();
  }
}, 200), [formikProps.values.safetyCoefficientIncreaseDrivingForceCustom
  , formikProps.values.slopeSurfaceMorphology
  , formikProps.values.externalLoads]);

const calculateYDF = () => {
  if (!Number.isNaN(formikProps.values.gR) && !Number.isNaN(formikProps.values.yD)) {
    formikProps.setFieldValue('yDF',
      Number((Number(formikProps.values.gR)
        * Number(formikProps.values.yD)).toFixed(2))
    );
  } else {
    formikProps.setFieldValue('yDF', 0);
  }
}

useEffect(() => {
  debounce(calculateYDF, 200)();
}, [formikProps.values.gR
  , formikProps.values.yD]);

const currentResults = useAppSelector((state: RootState) => {
  return { ...state.Macro1.results.data };
});
useEffect(() => {
  if (currentResults.validation?.startValidation && !currentResults.validation?.safetyCoefficients) {
    formikProps.submitForm();
    formikProps.validateForm().then((errors) => {
      if (currentResults.validation)
        dispatch(updateValidation({
          ...currentResults.validation,
          safetyCoefficients: true,
          safetyCoefficientsErrors: Object.keys(errors).length > 0,
        }));
    });

  }
}, [JSON.stringify(currentResults.validation)]);

return (
  <div className='SafetyCoefficientsFormContainer style-form'>
    <FormikProvider value={formikProps}>  
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <label className='form-section-title'>{t('safety_coefficient_section_affecting_stabilizing_forces')}</label>
        </Grid>
        <Grid item xs={11}>
          <div className="d-flex">
            <label>{t('safety_coefficient_uncertainty_thickness_surficial_instability', { unit_measure: '' })}</label>
            <i onClick={() => { openDialog('uncertainty_thickness_surficial_stability') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
          </div>
          <FieldNumber name="uncertaintyThicknessSurficialInstability" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="uncertaintyThicknessSurficialInstability" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_uncertainty_thickness_surficial_instability_symbol")}</label>
        </div>
        <Grid item xs={11}>
          <div className="d-flex">
            <label>{t('safety_coefficient_uncertainty_rock_mass_unit_weight', { unit_measure: '' })}</label>
            <i onClick={() => { openDialog('uncertainty_rock_mass_unit') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
          </div>
          <FieldNumber name="uncertaintyRockMassUnitWeight" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="uncertaintyRockMassUnitWeight" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_uncertainty_rock_mass_unit_weight_symbol")}</label>
        </div>
        <Grid item xs={11}>
          <div className="d-flex">
            <label>{t('safety_coefficient_uncertainty_rock_behavior_and_weathering', { unit_measure: '' })}</label>
            <i onClick={() => { openDialog('uncertainty_rock_behaviour') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
          </div>
          <FieldNumber name="uncertaintyRockBehaviorAndWeathering" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="uncertaintyRockBehaviorAndWeathering" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_uncertainty_rock_behavior_and_weathering_symbol")}</label>
        </div>
        <hr className="form-horizontal-divider" />
        <Grid item xs={12}>
          <label className='form-section-title'>{t('safety_coefficient_section_reduce_stabilizing_forces')}</label>
        </Grid>
        <Grid item xs={12}>
          <label>{t('safety_coefficient_reduce_stabilizing_force_custom', { unit_measure: '' })}</label>
          <Field type="checkbox" name="safetyCoefficientReduceStabilizingForceCustom" disabled={props.readOnly}/>
          <ErrorMessage name="safetyCoefficientReduceStabilizingForceCustom" component="div" className="text-danger" />
        </Grid>
        <Grid item xs={11}>
          <label>{t('safety_coefficient_gR', { unit_measure: '' })}<label className="symbol formula">{t("safety_coefficient_gR_formula")}</label></label>
          <FieldNumber name="gR" disabled={props.readOnly ? true : !formikProps.getFieldProps('safetyCoefficientReduceStabilizingForceCustom').value} step="0.01" />
          <ErrorMessage name="gR" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_gR_symbol")}</label>
        </div>
        <hr className="form-horizontal-divider" />
        <Grid item xs={12}>
          <label className='form-section-title'>{t('safety_coefficient_section_affecting_driving_forces')}</label>
        </Grid>
        <Grid item xs={11}>
          <div className="d-flex">
            <label>{t('safety_coefficient_slope_surface_morphology', { unit_measure: '' })}</label>
            <i onClick={() => { openDialog('slope_surface_morphology') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
          </div>
          <FieldNumber name="slopeSurfaceMorphology" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="slopeSurfaceMorphology" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_slope_surface_morphology_symbol")}</label>
        </div>
        <Grid item xs={11}>
          <div className="d-flex">
            <label>{t('safety_coefficient_external_loads', { unit_measure: '' })}</label>
            <i onClick={() => { openDialog('external_loads') }} className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon' />
          </div>
          <FieldNumber name="externalLoads" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="externalLoads" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_external_loads_symbol")}</label>
        </div>
        <Grid item xs={12}>
          <label>{t('safety_coefficient_increase_driving_force_custom', { unit_measure: '' })}</label>
          <Field type="checkbox" name="safetyCoefficientIncreaseDrivingForceCustom" disabled={props.readOnly}/>
          <ErrorMessage name="safetyCoefficientIncreaseDrivingForceCustom" component="div" className="text-danger" />
        </Grid>
        <Grid item xs={11}>
          <label>{t('safety_coefficient_yD', { unit_measure: '' })}<label className="symbol formula">{t("safety_coefficient_yD_formula")}</label></label>
          <FieldNumber name="yD" disabled={props.readOnly ? true : !formikProps.getFieldProps('safetyCoefficientIncreaseDrivingForceCustom').value} step="0.01" />
          <ErrorMessage name="yD" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_yD_symbol")}</label>
        </div>
        <hr className="form-horizontal-divider" />
        <Grid item xs={12}>
          <label className='form-section-title'>{t('safety_coefficient_section_affecting_driving_forces')}</label>
        </Grid>
        <Grid item xs={11}>
          <label>{t('safety_coefficient_yDF', { unit_measure: '' })}<label className="symbol formula">{t("safety_coefficient_yDF_formula")}</label></label>
          <FieldNumber name="yDF" disabled step="0.01" />
          <ErrorMessage name="yDF" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_yDF_symbol")}</label>
        </div>
        <hr className="form-horizontal-divider" />
        <Grid item xs={12}>
          <label className='form-section-title'>{t('safety_coefficient_section_mesh')}</label>
        </Grid>
        <Grid item xs={10} sx={{marginRight: "-5px"}}>
          <label>{t('safety_coefficient_coefficient_punch_resistance', { unit_measure: '' })}</label>
          <FieldNumber name="coefficientPunchResistance" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="coefficientPunchResistance" component="div" className="text-danger" />
        </Grid>
        <div style={{ marginLeft: "auto" }}>
          <label className="symbol text-nowrap">{t("safety_coefficient_coefficient_punch_resistance_symbol")}</label>
        </div>
        <Grid item xs={12}>
          <label className='form-section-title'>{t('safety_coefficient_section_anchor_type')}</label>
        </Grid>
        <Grid item xs={11}>
          <label>{t('safety_coefficient_coefficient_yield_stress_bar', { unit_measure: '' })}</label>
          <FieldNumber name="coefficientYieldStressBar" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="coefficientYieldStressBar" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_coefficient_yield_stress_bar_symbol")}</label>
        </div>
        <Grid item xs={11}>
          <label>{t('safety_coefficient_coefficient_bond_stress', { unit_measure: '' })}</label>
          <FieldNumber name="coefficientBondStress" step="0.01" disabled={props.readOnly}/>
          <ErrorMessage name="coefficientBondStress" component="div" className="text-danger" />
        </Grid>
        <div className="symbol-wrap">
          <label className="symbol">{t("safety_coefficient_coefficient_bond_stress_symbol")}</label>
        </div>
      </Grid>
    </FormikProvider>
    <Dialog
      open={CurrentDialog == 'uncertainty_rock_behaviour'}
      title={`${t('macro1_info_dialogs_uncertainty_rock_behaviour')}`}
      onClose={closeDialog}>
      <div>
        <img
          width='100%'
          height='100%'
          srcSet={`${imgUncertaintyRockBehaviour}?fit=crop&auto=format&dpr=2 2x`}
          src={`${imgUncertaintyRockBehaviour}?fit=crop&auto=format&dpr=2 2x`}
          alt="Uncertainty of rock behavior and weathering"></img>
      </div>
    </Dialog>
    <Dialog
      open={CurrentDialog == 'uncertainty_rock_mass_unit'}
      title={`${t('macro1_info_dialogs_uncertainty_rock_mass_unit')}`}
      onClose={closeDialog}>
      <div>
        <img
          width='100%'
          height='100%'
          srcSet={`${imgUncertaintyRockMassUnit}?fit=crop&auto=format&dpr=2 2x`}
          src={`${imgUncertaintyRockMassUnit}?fit=crop&auto=format&dpr=2 2x`}
          alt="Uncertainty of the rock mass unit weight"></img>
      </div>
    </Dialog>
    <Dialog
      open={CurrentDialog == 'uncertainty_thickness_surficial_stability'}
      title={`${t('macro1_info_dialogs_uncertainty_thickness_surficial_stability')}`}
      onClose={closeDialog}>
      <div>
        <img
          width='100%'
          height='100%'
          srcSet={`${imgUncertaintyThicknessSurficialStability}?fit=crop&auto=format&dpr=2 2x`}
          src={`${imgUncertaintyThicknessSurficialStability}?fit=crop&auto=format&dpr=2 2x`}
          alt="Uncertainty of the thickness of surficial insability"></img>
      </div>
    </Dialog>
    <Dialog
      open={CurrentDialog == 'slope_surface_morphology'}
      title={`${t('macro1_info_dialogs_slope_surface_morphology')}`}
      onClose={closeDialog}>
      <div>
        <img
          width='100%'
          height='100%'
          srcSet={`${imgSlopeSurfaceMorphology}?fit=crop&auto=format&dpr=2 2x`}
          src={`${imgSlopeSurfaceMorphology}?fit=crop&auto=format&dpr=2 2x`}
          alt="Slope Surface Morphology"></img>
      </div>
    </Dialog>
    <Dialog
      open={CurrentDialog == 'external_loads'}
      title={`${t('macro1_info_dialogs_external_loads')}`}
      onClose={closeDialog}>
      <div>
        <img
          width='100%'
          height='100%'
          srcSet={`${imgExternalLoads}?fit=crop&auto=format&dpr=2 2x`}
          src={`${imgExternalLoads}?fit=crop&auto=format&dpr=2 2x`}
          alt="External Loads"></img>
      </div>
    </Dialog>
  </div >
);
};

export default SafetyCoefficients;
